import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './AccountCheck.css';
import { logout, checkAuth, handleAuthError } from '../utils/auth';
import { API_URL } from '../config/data';

const AccountCheck = () => {
  const [user, setUser] = useState(null);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [tradingAccounts, setTradingAccounts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const [step, setStep] = useState('methods'); // methods | form
  const [openMethod, setOpenMethod] = useState(null); // sbp | invoice | null
  const [selectedMethod, setSelectedMethod] = useState(null); // sbp | invoice

  const [formData, setFormData] = useState({
    tradingAccountId: '',
    bankAccountId: '',
    amount: ''
  });

  const [selectedTradingAccount, setSelectedTradingAccount] = useState(null);
  const [selectedBankAccount, setSelectedBankAccount] = useState(null);

  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [showSbpQrModal, setShowSbpQrModal] = useState(false);
  const [sbpQrImage, setSbpQrImage] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const getAuthToken = () => localStorage.getItem('authToken');

  const fetchUserProfile = async () => {
    const token = getAuthToken();
    if (!token) throw new Error('Токен авторизации не найден');

    const response = await axios.get(`${API_URL}/api/profile`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    return response.data.user;
  };

  const fetchBankAccounts = async () => {
    const token = getAuthToken();
    if (!token) throw new Error('Токен авторизации не найден');

    const response = await axios.get(`${API_URL}/api/accounts`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    return response.data.accounts;
  };

  const fetchTradingAccounts = async () => {
    const token = getAuthToken();
    if (!token) throw new Error('Токен авторизации не найден');

    const response = await axios.get(`${API_URL}/api/trading-accounts`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    return response.data.accounts;
  };

  const findTradingAccountById = (accountId) => {
    if (!accountId || !tradingAccounts.length) {
      setSelectedTradingAccount(null);
      return;
    }

    const foundAccount = tradingAccounts.find(
      (account) => account.id.toString() === accountId
    );
    setSelectedTradingAccount(foundAccount || null);
  };

  const findBankAccountById = (accountId) => {
    if (!accountId || !bankAccounts.length) {
      setSelectedBankAccount(null);
      return;
    }

    const foundAccount = bankAccounts.find(
      (account) => account.id.toString() === accountId
    );
    setSelectedBankAccount(foundAccount || null);
  };

  const handleFormDataChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value
    }));

    if (field === 'tradingAccountId') {
      findTradingAccountById(value);
    }

    if (field === 'bankAccountId') {
      findBankAccountById(value);
    }
  };

  const resetForm = () => {
    setFormData({
      tradingAccountId: '',
      bankAccountId: '',
      amount: ''
    });
    setSelectedTradingAccount(null);
    setSelectedBankAccount(null);
  };

  const getUserDisplayName = () => {
    if (!user) return 'Загрузка...';
    return `${user.lastName} ${user.firstName}`;
  };

  const getUserPosition = () => {
    if (!user) return 'Загрузка...';
    return user.role || 'Квалифицированный управляющий';
  };

  const formatBalance = (amount) => {
    return new Intl.NumberFormat('ru-RU').format(amount);
  };

  const handleSuccessPopupClose = (e) => {
    if (e.target === e.currentTarget) {
      setShowSuccessPopup(false);
    }
  };

  const handleSuccessButtonClick = () => {
    setShowSuccessPopup(false);
  };

  const handleSbpQrModalClose = (e) => {
    if (e.target === e.currentTarget) {
      setShowSbpQrModal(false);
    }
  };

  const toggleMethod = (method) => {
    setOpenMethod((prev) => (prev === method ? null : method));
  };

  const selectPaymentMethod = (method) => {
    setSelectedMethod(method);
    resetForm();
    setStep('form');
  };

  const goBackToMethods = () => {
    setStep('methods');
    setSelectedMethod(null);
    resetForm();
  };

  const createInvoiceDeposit = async (e) => {
    e.preventDefault();

    if (!selectedTradingAccount) {
      alert('Пожалуйста, выберите торговый счет для пополнения');
      return;
    }

    if (!selectedBankAccount) {
      alert('Пожалуйста, выберите банковский счет для списания');
      return;
    }

    try {
      setIsSubmitting(true);
      const token = getAuthToken();
      if (!token) throw new Error('Токен авторизации не найден');

      const depositData = {
        amount: 0,
        currency: selectedTradingAccount.currency,
        comment: 'Пополнение торгового счета',
        tradingAccountId: selectedTradingAccount.id,
        paymentMethod: 'invoice',
        recipientDetails: {
          accountNumber: selectedTradingAccount.account_number,
          currentBalance: selectedTradingAccount.balance,
          bankAccountNumber: selectedBankAccount.account_number || selectedBankAccount.number
        }
      };

      const response = await axios.post(`${API_URL}/api/operations/deposit`, depositData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.data.message === 'Deposit request created successfully') {
        resetForm();
        setShowSuccessPopup(true);
      }
    } catch (error) {
      console.error('Ошибка при создании депозита:', error);
      if (handleAuthError(error)) return;
      alert('Ошибка при создании запроса на пополнение. Попробуйте еще раз.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const createSbpDeposit = async (e) => {
    e.preventDefault();

    if (!selectedTradingAccount) {
      alert('Пожалуйста, выберите торговый счет для пополнения');
      return;
    }

    const amountValue = parseFloat(String(formData.amount).replace(',', '.'));
    if (!amountValue || amountValue <= 0) {
      alert('Пожалуйста, укажите сумму пополнения');
      return;
    }

    if (amountValue > 100000) {
      alert('Максимальный перевод за одну операцию — 100 000 руб.');
      return;
    }

    try {
      setIsSubmitting(true);
      const token = getAuthToken();
      if (!token) throw new Error('Токен авторизации не найден');

      const depositData = {
        amount: amountValue,
        currency: selectedTradingAccount.currency || 'RUB',
        comment: 'Пополнение через СБП',
        tradingAccountId: selectedTradingAccount.id,
        paymentMethod: 'sbp',
        recipientDetails: {
          accountNumber: selectedTradingAccount.account_number,
          currentBalance: selectedTradingAccount.balance,
          amount: amountValue
        }
      };

      const [depositResponse, qrResponse] = await Promise.all([
        axios.post(`${API_URL}/api/operations/deposit`, depositData, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }),
        axios.get(`${API_URL}/api/operations/sbp-qr`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }).catch(() => ({ data: { qrImage: null, uploaded: false } }))
      ]);

      if (depositResponse.data.message === 'Deposit request created successfully') {
        setSbpQrImage(qrResponse.data?.qrImage || null);
        setShowSbpQrModal(true);
      }
    } catch (error) {
      console.error('Ошибка при создании депозита СБП:', error);
      if (handleAuthError(error)) return;
      alert('Ошибка при создании запроса на пополнение. Попробуйте еще раз.');
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (!checkAuth()) return;

    const loadData = async () => {
      try {
        setIsLoading(true);
        const [userData, bankAccountsData, tradingAccountsData] = await Promise.all([
          fetchUserProfile(),
          fetchBankAccounts(),
          fetchTradingAccounts()
        ]);

        setUser(userData);
        setBankAccounts(bankAccountsData);
        setTradingAccounts(tradingAccountsData);
      } catch (error) {
        console.error('Ошибка при загрузке данных:', error);
        if (handleAuthError(error)) return;
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, []);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        if (showSbpQrModal) setShowSbpQrModal(false);
        if (showSuccessPopup) setShowSuccessPopup(false);
      }
    };

    if (showSuccessPopup || showSbpQrModal) {
      document.addEventListener('keydown', handleEscape);
      document.body.classList.add('popup-open');
      return () => {
        document.removeEventListener('keydown', handleEscape);
        document.body.classList.remove('popup-open');
      };
    }
  }, [showSuccessPopup, showSbpQrModal]);

  if (isLoading) {
    return (
      <div className="wrapper">
        <div className="loading">
          <p>Загрузка данных...</p>
        </div>
      </div>
    );
  }

  const formTitle =
    selectedMethod === 'sbp' ? 'Пополнение через СБП' : 'Выставить счёт';

  return (
    <div className="wrapper">
      <header data-fls-header-accaunt="" className="header-accaunt">
        <div className="header-accaunt__container">
          <div className="header-accaunt__body">
            <a href="/account" className="header-accaunt__logo">
              <img src="/assets/img/logo.svg" alt="Logo" />
            </a>
            <div className="header-accaunt__actions">
              <div className="header-accaunt__settings">
                <a href="/account/setting" className="--icon-settings"></a>
              </div>
              <div className="header-accaunt__profile">
                <a href="" className="--icon-profile">
                  <div className="header-accaunt__content-profile">
                    <span>{getUserDisplayName()}</span>
                    <span>{getUserPosition()}</span>
                  </div>
                </a>
              </div>
              <button
                data-fls-button=""
                type="button"
                className="header-accaunt__logout header-accaunt__logout--icon-log-out"
                onClick={logout}
              ></button>
            </div>
          </div>
        </div>
      </header>
      <main className="page account">
        <div className="account__container">
          <div className="account__body">
            <nav data-fls-block="" data-fls-aside="" className="aside">
              <ul className="aside__list">
                <li className="aside__item">
                  <a href="/account" className="aside__link aside__link--icon-home">Главная</a>
                </li>
                <li className="aside__item">
                  <a href="/account/expense" className="aside__link aside__link--active aside__link--icon-wallet">Счета</a>
                </li>
                <li className="aside__item">
                  <a href="/account/history" className="aside__link aside__link--icon-history">История</a>
                </li>
                <li className="aside__item">
                  <a href="#" className="aside__link aside__link--icon-withdraw">Вывести</a>
                </li>
              </ul>
              <div className="aside__actions">
                <a href="/account/withdraw" className="aside__action aside__action--icon-withdraw">Вывести деньги</a>
                <a href="/account/check" className="aside__action aside__action--icon-deposit">Пополнить баланс</a>
              </div>
            </nav>
            <div className="account__content">
              {step === 'methods' && (
                <section className="deposit-methods">
                  <h3 className="account__title account__title--title-25 deposit-methods__page-title">
                    Пополнение баланса
                  </h3>
                  <div className="deposit-methods__card">
                    <h2 className="deposit-methods__subtitle">Выберите способ пополнения</h2>

                    <div
                      className={`deposit-method${openMethod === 'sbp' ? ' is-open' : ''}`}
                    >
                      <button
                        type="button"
                        className="deposit-method__summary"
                        onClick={() => toggleMethod('sbp')}
                      >
                        <div className="deposit-method__header">
                          <img
                            className="deposit-method__icon"
                            src="/assets/img/sbp.svg"
                            alt="СБП"
                          />
                          <span className="deposit-method__name">СБП</span>
                          <svg
                            className="deposit-method__chevron"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            aria-hidden="true"
                          >
                            <path
                              d="M6 9L12 15L18 9"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </div>
                        <div className="deposit-method__desc">
                          <div className="deposit-method__row">
                            <span className="deposit-method__label">Комиссия:</span>
                            <span className="deposit-method__value">0%</span>
                          </div>
                          <div className="deposit-method__row">
                            <span className="deposit-method__label">Валюта:</span>
                            <span className="deposit-method__value">RUB</span>
                          </div>
                          <div className="deposit-method__row">
                            <span className="deposit-method__label">Срок зачисления:</span>
                            <span className="deposit-method__value">несколько минут</span>
                          </div>
                          <p className="deposit-method__note">
                            Пополнение с электронных кошельков невозможно, используйте полноценные банковские счета для перевода средств
                          </p>
                        </div>
                      </button>
                      {openMethod === 'sbp' && (
                        <div className="deposit-method__body">
                          <button
                            type="button"
                            className="deposit-method__select"
                            onClick={() => selectPaymentMethod('sbp')}
                          >
                            Пополнить через СБП
                          </button>
                        </div>
                      )}
                    </div>

                    <div
                      className={`deposit-method${openMethod === 'invoice' ? ' is-open' : ''}`}
                    >
                      <button
                        type="button"
                        className="deposit-method__summary"
                        onClick={() => toggleMethod('invoice')}
                      >
                        <div className="deposit-method__header">
                          <img
                            className="deposit-method__icon"
                            src="/assets/img/invoice-bank.svg"
                            alt="Выставить счёт"
                          />
                          <span className="deposit-method__name">Выставить счёт</span>
                          <svg
                            className="deposit-method__chevron"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            aria-hidden="true"
                          >
                            <path
                              d="M6 9L12 15L18 9"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </div>
                        <div className="deposit-method__desc">
                          <div className="deposit-method__row">
                            <span className="deposit-method__label">Комиссия:</span>
                            <span className="deposit-method__value">рассчитывается вашим банком</span>
                          </div>
                          <div className="deposit-method__row">
                            <span className="deposit-method__label">Валюта:</span>
                            <span className="deposit-method__value">любая валюта</span>
                          </div>
                          <div className="deposit-method__row">
                            <span className="deposit-method__label">Срок зачисления:</span>
                            <span className="deposit-method__value">1-2 рабочих дня</span>
                          </div>
                          <p className="deposit-method__note">
                            Пополнение с электронных кошельков невозможно, используйте полноценные банковские счета для перевода средств
                          </p>
                        </div>
                      </button>
                      {openMethod === 'invoice' && (
                        <div className="deposit-method__body">
                          <button
                            type="button"
                            className="deposit-method__select"
                            onClick={() => selectPaymentMethod('invoice')}
                          >
                            Выставить счёт
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </section>
              )}

              {step === 'form' && (
                <>
                  <button
                    type="button"
                    className="deposit-back"
                    onClick={goBackToMethods}
                  >
                    ← Назад к способам оплаты
                  </button>
                  <h3 className="account__title account__title--title-25">{formTitle}</h3>
                  <form
                    onSubmit={selectedMethod === 'sbp' ? createSbpDeposit : createInvoiceDeposit}
                    data-fls-form=""
                    data-fls-maxwidth=""
                    className="account__check check"
                  >
                    <div data-fls-block="" className="check__block">
                      <h4 className="check__subtitle check__subtitle--title-20">
                        Торговый счет для пополнения
                      </h4>
                      <div className="check__inputs">
                        <select
                          data-fls-input=""
                          required
                          name="tradingAccountId"
                          value={formData.tradingAccountId}
                          onChange={(e) => handleFormDataChange('tradingAccountId', e.target.value)}
                          data-error="Error"
                          className="input"
                        >
                          <option value="">Выберите торговый счет</option>
                          {tradingAccounts.map((account) => {
                            const totalBalance = parseFloat(account.balance) || 0;
                            return (
                              <option key={account.id} value={account.id}>
                                {account.account_number} ({account.currency}) - {formatBalance(totalBalance)} {account.currency}
                              </option>
                            );
                          })}
                        </select>
                        <div className="check__plsdata plsdata">
                          <input
                            data-fls-input=""
                            autoComplete="off"
                            type="text"
                            name="plsdata"
                            data-error="Error"
                            className="plsdata__input input"
                            disabled
                            value=""
                          />
                          <div className="plsdata__content">
                            Текущий баланс:{' '}
                            {selectedTradingAccount
                              ? `${formatBalance(parseFloat(selectedTradingAccount.balance) || 0)} ${selectedTradingAccount.currency}`
                              : 'Счет не выбран'}
                          </div>
                        </div>
                      </div>
                    </div>

                    {selectedMethod === 'invoice' && (
                      <div data-fls-block="" className="check__block">
                        <h4 className="check__subtitle check__subtitle--title-20">
                          Банковский счет для списания
                        </h4>
                        <div className="check__text">
                          <p>Выберите банковский счет, с которого планируете отправить средства</p>
                        </div>
                        <div className="check__inputs">
                          <select
                            data-fls-input=""
                            required
                            name="bankAccountId"
                            value={formData.bankAccountId}
                            onChange={(e) => handleFormDataChange('bankAccountId', e.target.value)}
                            data-error="Error"
                            className="input"
                          >
                            <option value="">Выберите банковский счет</option>
                            {bankAccounts.map((account) => (
                              <option key={account.id} value={account.id}>
                                {account.number} {account.bank} ({account.currency})
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    )}

                    {selectedMethod === 'sbp' && (
                      <div data-fls-block="" className="check__block">
                        <h4 className="check__subtitle check__subtitle--title-20">Сумма</h4>
                        <div className="check__text">
                          <p>Укажите сумму пополнения в рублях (максимум 100 000 руб.)</p>
                        </div>
                        <div className="check__inputs">
                          <input
                            data-fls-input=""
                            required
                            type="number"
                            name="amount"
                            min="1"
                            max="100000"
                            step="0.01"
                            placeholder="Введите сумму"
                            value={formData.amount}
                            onChange={(e) => handleFormDataChange('amount', e.target.value)}
                            data-error="Error"
                            className="input"
                          />
                        </div>
                      </div>
                    )}

                    <button
                      type="submit"
                      data-fls-button=""
                      className="check__button button"
                      disabled={isSubmitting}
                    >
                      {isSubmitting
                        ? 'Отправка...'
                        : selectedMethod === 'sbp'
                          ? 'Перейти к оплате'
                          : 'Сформировать счет'}
                    </button>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      </main>

      {showSuccessPopup && (
        <div
          data-fls-popup="success"
          aria-hidden="false"
          className={`popup ${showSuccessPopup ? 'show' : ''}`}
          onClick={handleSuccessPopupClose}
        >
          <div data-fls-popup-wrapper="" className="popup__wrapper">
            <div data-fls-popup-body="" className="popup__body success">
              <h3 className="success__title success__title--title-20">Успешно!</h3>
              <div className="success__content">
                <p className="success__message">
                  Заявка сформирована, в ближайшее время с вами свяжется администратор для выполнения платежа.
                </p>
              </div>
              <div className="success__actions">
                <button
                  data-fls-button=""
                  type="button"
                  className="success__button button"
                  onClick={handleSuccessButtonClick}
                >
                  Понятно
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showSbpQrModal && (
        <div
          className="popup show sbp-qr-popup"
          onClick={handleSbpQrModalClose}
        >
          <div className="popup__wrapper sbp-qr-popup__wrapper">
            <div className="popup__body sbp-qr-modal">
              <button
                type="button"
                className="sbp-qr-modal__close"
                aria-label="Закрыть"
                onClick={() => setShowSbpQrModal(false)}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M6 6L18 18M18 6L6 18" stroke="#333" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </button>

              {sbpQrImage ? (
                <img
                  className="sbp-qr-modal__qr"
                  src={sbpQrImage}
                  alt="QR-код СБП"
                />
              ) : (
                <div className="sbp-qr-modal__empty">
                  QR-Код не загружен - попробуйте другой метод оплаты
                </div>
              )}

              <div className="sbp-qr-modal__text">
                <p>Отсканируйте в мобильном приложении СберБанк Онлайн</p>
                <p className="sbp-qr-modal__limit">
                  Максимальный перевод за одну операцию 100 000 руб.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountCheck;
